import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Result, Button, Typography, Space } from 'antd'
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons'
import styled, { keyframes } from 'styled-components'
import api from '../utils/api'
import { CLIENT_PUBLIC_SITE_URL } from '../utils/clientPublicSiteUrl'
import { formatTz } from '../utils/timezone'

const { Title, Text } = Typography

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`

const ResultContainer = styled.div`
  min-height: 100vh;
  width: 100vw;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.5s ease-out;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0;
`

const ResultCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  padding: 32px;
  text-align: center;
  width: 100%;
  max-width: 480px;
  min-width: 320px;
  margin: 0 16px;
`

const SuccessIcon = styled(CheckCircleFilled)`
  font-size: 64px;
  color: #52c41a;
  margin-bottom: 24px;
  animation: ${pulse} 1s ease-in-out infinite;
`

const ErrorIcon = styled(CloseCircleFilled)`
  font-size: 64px;
  color: #ff4d4f;
  margin-bottom: 24px;
`

const BusinessName = styled(Title)`
  && {
    color: #888;
    margin-bottom: 20px;
    font-size: 18px;
    font-weight: 400;
  }
`

const PaymentResult = () => {
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [pendingHint, setPendingHint] = useState('')
  const [retryInSeconds, setRetryInSeconds] = useState(0)

  const orderSn = searchParams.get('order_sn')
  const returnStatusQ = searchParams.get('status')
  const returnReasonQ = searchParams.get('reason')

  useEffect(() => {
    /** Return 失敗時後端會導向 ?status=error&reason=...，避免一直顯示「確認中」 */
    if (returnStatusQ === 'error') {
      const reason = returnReasonQ
      const reasonText =
        reason === 'payment_not_found'
          ? '找不到此筆訂單，請向店家確認連結是否正確。'
          : reason === 'payment_process_failed'
            ? '藍新回傳處理異常。若已扣款請聯繫客服，或稍後重新整理此頁確認。'
            : '無法完成付款確認。'
      setError(reasonText)
      setLoading(false)
      return undefined
    }

    let cancelled = false
    let refreshTimer = null
    let countdownTimer = null

    const normalizeStatus = (status) => String(status || '').toUpperCase()

    /** 後端 Payment 模型使用 completed / failed / pending（小寫），與舊版 PAID 並存 */
    const isSuccessStatus = (s) =>
      s === 'COMPLETED' || s === 'PAID' || s === 'SUCCESS'
    const isFailedStatus = (s) =>
      s === 'FAILED' ||
      s === 'FAIL' ||
      s === 'CANCELLED' ||
      s === 'CANCELED'

    const scheduleRetry = (seconds = 4) => {
      setRetryInSeconds(seconds)
      countdownTimer = setInterval(() => {
        setRetryInSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimer)
            return 0
          }
          return prev - 1
        })
      }, 1000)
      refreshTimer = setTimeout(() => {
        checkPaymentResult()
      }, seconds * 1000)
    }

    const checkPaymentResult = async () => {
      if (!orderSn) {
        setError('無效的訂單編號')
        setLoading(false)
        return
      }

      try {
        // 以 query 版為主，並保留 path 版 fallback 相容
        let response = null
        try {
          response = await api.get('/api/payments/quick-payment/result', {
            params: { order_sn: orderSn }
          })
        } catch (queryErr) {
          response = await api.get(`/api/payments/quick-payment/result/${orderSn}`)
        }

        if (cancelled) return

        const payload = response?.data?.data || {}
        const status = normalizeStatus(payload.status)
        setResult(payload)
        setError(null)

        if (status === 'PENDING') {
          setPendingHint(payload.hint || '付款結果尚在確認中，系統將自動重新查詢。')
          scheduleRetry(4)
          return
        }

        setPendingHint('')
        setRetryInSeconds(0)

        if (isFailedStatus(status)) {
          setError(payload.hint || response?.data?.message || '支付失敗')
          return
        }

        if (status && !isSuccessStatus(status)) {
          setError(payload.hint || response?.data?.message || '支付未完成')
        }
      } catch (error) {
        if (cancelled) return
        console.error('獲取支付結果失敗:', error)
        setError(error.response?.data?.message || '獲取支付結果失敗')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    checkPaymentResult()
    return () => {
      cancelled = true
      if (refreshTimer) clearTimeout(refreshTimer)
      if (countdownTimer) clearInterval(countdownTimer)
    }
  }, [orderSn, returnStatusQ, returnReasonQ])

  if (loading) {
    return (
      <ResultContainer>
        <ResultCard>
          <Space direction='vertical' size='large' style={{ width: '100%' }}>
            <Title level={3}>正在確認支付結果...</Title>
            <Text type='secondary'>請稍候，我們正在處理您的支付信息</Text>
          </Space>
        </ResultCard>
      </ResultContainer>
    )
  }

  if (error) {
    return (
      <ResultContainer>
        <ResultCard>
          <ErrorIcon />
          {result?.business_name && (
            <Title level={5} style={{ color: '#888', marginBottom: 8 }}>
              {result.business_name}
            </Title>
          )}
          <Title level={3}>支付失敗</Title>
          <Text type='secondary'>{error}</Text>
          <Space style={{ marginTop: 24 }} wrap>
            <Button
              type='primary'
              onClick={() => {
                window.location.href = CLIENT_PUBLIC_SITE_URL
              }}
            >
              回到首頁
            </Button>
            <Button onClick={() => window.close()}>關閉頁面</Button>
          </Space>
        </ResultCard>
      </ResultContainer>
    )
  }

  return (
    <ResultContainer>
      <ResultCard>
        {pendingHint ? <ErrorIcon style={{ color: '#faad14' }} /> : <SuccessIcon />}
        {result?.business_name && (
          <BusinessName level={5}>{result.business_name}</BusinessName>
        )}
        <Title level={3} style={{ marginBottom: 24 }}>
          {pendingHint ? '付款確認中' : '支付成功'}
        </Title>
        <Space direction='vertical' size='large' style={{ width: '100%' }}>
          {pendingHint && (
            <div>
              <Text type='secondary'>{pendingHint}</Text>
              <br />
              <Text type='secondary'>約 {retryInSeconds} 秒後自動重查</Text>
            </div>
          )}
          <div>
            <Text type='secondary'>訂單編號</Text>
            <br />
            <Text strong>{result?.order_sn}</Text>
          </div>
          <div>
            <Text type='secondary'>支付金額</Text>
            <br />
            <Text strong style={{ fontSize: '24px' }}>
              NT$ {result?.amount?.toLocaleString()}
            </Text>
          </div>
          <div>
            <Text type='secondary'>支付時間</Text>
            <br />
            <Text strong>
              {formatTz(result?.updated_at, 'YYYY/MM/DD HH:mm:ss')}
            </Text>
          </div>
          <Space
            direction='vertical'
            size='large'
            style={{ width: '100%', marginTop: '24px' }}
          >
            <Text style={{ color: '#999' }}>
              請截圖留存頁面，並於支付完成後向店家出示畫面。
            </Text>
          </Space>
          <Space style={{ marginTop: 12 }}>
            <Button
              type='primary'
              style={{ width: '200px', height: '50px' }}
              onClick={() => {
                window.location.href = CLIENT_PUBLIC_SITE_URL
              }}
            >
              回到首頁
            </Button>
          </Space>
        </Space>
      </ResultCard>
    </ResultContainer>
  )
}

export default PaymentResult
